import React, { useEffect, useState } from "react";
import CountUp from "react-countup";
import { getUsers } from "../services/api";

import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler
} from "chart.js";

import { Doughnut, Line } from "react-chartjs-2";
import { FaUserMd, FaHeartbeat, FaExclamationTriangle, FaChartLine } from "react-icons/fa";

// Register ChartJS components
ChartJS.register(
    ArcElement,
    Tooltip,
    Legend,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler
);

export default function Dashboard() {
    const [users, setUsers] = useState([]);
    const [pieData, setPieData] = useState(null);
    const [lineData, setLineData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        try {
            const data = await getUsers();
            setUsers(data);

            let low = 0, medium = 0, high = 0;
            let dateMap = {};

            data.forEach((u) => {
                // 1. Corrected path: matches "u.data.Risk_Level"
                const risk = u.data?.Risk_Level || "";
                const date = new Date(u.createdDate).toLocaleDateString();

                // 2. Matching partial strings since your data says "Mild metabolic risk"
                if (risk.toLowerCase().includes("mild")) low++;
                else if (risk.toLowerCase().includes("moderate")) medium++;
                else if (risk.toLowerCase().includes("high")) high++;

                dateMap[date] = (dateMap[date] || 0) + 1;
            });

            // 3. Fixed Variable names (low, medium, high)
            setPieData({
                labels: ["Mild Risk", "Moderate Risk", "High Risk"],
                datasets: [
                    {
                        data: [low, medium, high],
                        backgroundColor: [
                            "rgba(34,197,94,0.85)",   // Green
                            "rgba(245,158,11,0.85)",  // Amber
                            "rgba(239,68,68,0.85)"    // Red
                        ],
                        borderColor: "#fff",
                        borderWidth: 4,
                        hoverOffset: 12,
                        spacing: 5,
                    }
                ]
            });

            setLineData({
                labels: Object.keys(dateMap),
                datasets: [
                    {
                        label: "Patient Registrations",
                        data: Object.values(dateMap),
                        borderColor: "#0d6efd",
                        backgroundColor: "rgba(13, 110, 253, 0.1)",
                        fill: true,
                        tension: 0.4,
                        pointRadius: 5,
                        pointBackgroundColor: "#0d6efd"
                    }
                ]
            });
        } catch (error) {
            console.error("Error loading dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    const pieOptions = {
        cutout: "70%",
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    padding: 20,
                    usePointStyle: true,
                    font: { size: 12 }
                },
            },
        },
    };

    const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: { beginAtZero: true, ticks: { stepSize: 1 } }
        }
    };

    // Statistical Calculations
    const total = users.length;
    
    const todayCount = users.filter(
        (u) => new Date(u.createdDate).toDateString() === new Date().toDateString()
    ).length;

    const highRiskCount = users.filter((u) =>
        u.data?.Risk_Level?.toLowerCase().includes("high")
    ).length;

    const avgFatty = users.length
        ? Math.round(
            users.reduce((sum, u) => sum + (Number(u.data?.Fatty_Liver_Probability) || 0), 0) / users.length
        )
        : 0;

    if (loading) return <div className="p-5 text-center">Optimizing Dashboard...</div>;

    return (
        <div className="container-fluid py-4">
            <h2 className="mb-4 fw-bold text-dark">Healthcare Analytics</h2>

            <div className="row g-4">
                {/* SIDEBAR STATS */}
                <div className="col-lg-3 col-md-4 d-flex flex-column gap-3">
                    <div className="glass-card p-3 d-flex align-items-center shadow-sm border-0">
                        <div className="bg-primary bg-opacity-10 p-3 rounded-3 me-3">
                            <FaUserMd size={24} className="text-primary" />
                        </div>
                        <div>
                            <small className="text-muted d-block uppercase fw-bold" style={{ fontSize: '10px' }}>TOTAL PATIENTS</small>
                            <h4 className="mb-0 fw-bold"><CountUp end={total} /></h4>
                        </div>
                    </div>

                    <div className="glass-card p-3 d-flex align-items-center shadow-sm border-0">
                        <div className="bg-success bg-opacity-10 p-3 rounded-3 me-3">
                            <FaHeartbeat size={24} className="text-success" />
                        </div>
                        <div>
                            <small className="text-muted d-block uppercase fw-bold" style={{ fontSize: '10px' }}>TODAY'S CASES</small>
                            <h4 className="mb-0 fw-bold"><CountUp end={todayCount} /></h4>
                        </div>
                    </div>

                    <div className="glass-card p-3 d-flex align-items-center shadow-sm border-0">
                        <div className="bg-danger bg-opacity-10 p-3 rounded-3 me-3">
                            <FaExclamationTriangle size={24} className="text-danger" />
                        </div>
                        <div>
                            <small className="text-muted d-block uppercase fw-bold" style={{ fontSize: '10px' }}>HIGH RISK CASES</small>
                            <h4 className="mb-0 fw-bold text-danger"><CountUp end={highRiskCount} /></h4>
                        </div>
                    </div>

                    <div className="glass-card p-3 d-flex align-items-center shadow-sm border-0">
                        <div className="bg-warning bg-opacity-10 p-3 rounded-3 me-3">
                            <FaChartLine size={24} className="text-warning" />
                        </div>
                        <div>
                            <small className="text-muted d-block uppercase fw-bold" style={{ fontSize: '10px' }}>AVG FATTY LIVER %</small>
                            <h4 className="mb-0 fw-bold">{avgFatty}%</h4>
                        </div>
                    </div>
                </div>

                {/* MAIN CHARTS */}
                <div className="col-lg-9 col-md-8">
                    <div className="row g-4">
                        {/* DONUT CHART */}
                        <div className="col-xl-5">
                            <div className="glass-card p-4 h-100 shadow-sm border-0">
                                <h6 className="fw-bold mb-4">Risk Distribution</h6>
                                {pieData ? (
                                    <div style={{ position: "relative", height: "300px" }}>
                                        <Doughnut data={pieData} options={pieOptions} />
                                        <div style={{
                                            position: "absolute",
                                            top: "30%",
                                            left: "50%",
                                            transform: "translate(-50%, -10%)",
                                            textAlign: "center",
                                        }}>
                                            <h3 className="mb-0 fw-bold">{total}</h3>
                                            <small className="text-muted">Total</small>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-center">No risk data available</p>
                                )}
                            </div>
                        </div>

                        {/* LINE CHART */}
                        <div className="col-xl-7">
                            <div className="glass-card p-4 h-100 shadow-sm border-0">
                                <h6 className="fw-bold mb-4">Registration Trend</h6>
                                {lineData ? (
                                    <div style={{ height: "300px" }}>
                                        <Line data={lineData} options={lineOptions} />
                                    </div>
                                ) : (
                                    <p className="text-center">No trend data available</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}